// The bundle's PBR declaration. A BecomingLit appearance decoder emits rendering
// VALUES — albedo, roughness, spec_vis, an 8-d NeuralBSDF feature — instead of
// RGB, and a light turns them into radiance. docs/OGS_PBR_PARITY.md.
// Notes: ~/comments/aos-threejs-poc/pbrManifest_parsePbrConfig_validation.md

/** Channel spans into the appr output, half-open `[start, end)`, as exported. */
export interface PbrChannelRanges {
  readonly albedo: readonly [number, number];
  readonly roughness: readonly [number, number];
  readonly specVis: readonly [number, number];
  readonly feature: readonly [number, number];
}

export interface PbrConfig {
  /** Branches whose appr emits a material. A branch absent from this list is RGB. */
  readonly branches: readonly string[];
  readonly apprChannels: number;
  readonly featDim: number;
  readonly channels: PbrChannelRanges;
  /** BecomingLit's roughness floor; `polface_specular` clamps alpha there anyway. */
  readonly minRoughness: number;
  readonly bsdf: string;
  /** The light the material was TRAINED under, when the bundle ships it. */
  readonly envSh27: Float32Array | null;
}

/** An RGB appr emits three planes; the lift's colour fetch assumes it. */
export const RGB_APPR_CHANNELS = 3;

const SPANS = [
  ["albedo", "albedo"],
  ["roughness", "roughness"],
  ["spec_vis", "specVis"],
  ["feature", "feature"],
] as const;

/** Parse `scene.pbr`. Absent or not enabled → null; DECLARED but malformed → throws. */
export function parsePbrConfig(raw: unknown): PbrConfig | null {
  if (!raw || typeof raw !== "object") return null;
  const pbr = raw as Record<string, unknown>;
  if (pbr.enabled !== true) return null;

  const branches = pbr.branches;
  if (!Array.isArray(branches) || branches.length === 0 || !branches.every((b) => typeof b === "string" && b)) {
    throw new Error("scene.json pbr: 'branches' must be a non-empty list of branch names");
  }
  const apprChannels = requirePositiveInt(pbr.appr_channels, "appr_channels");
  const featDim = requirePositiveInt(pbr.feat_dim, "feat_dim");
  const bsdf = pbr.bsdf;
  if (typeof bsdf !== "string" || !bsdf) throw new Error("scene.json pbr: 'bsdf' must name the NeuralBSDF ONNX");
  const minRoughness = Number(pbr.min_roughness);
  if (!Number.isFinite(minRoughness) || minRoughness < 0 || minRoughness >= 1) {
    throw new Error(`scene.json pbr: bad min_roughness ${String(pbr.min_roughness)}`);
  }
  // Only sRGB is implemented; a bundle asking for another transform would be
  // encoded by a shader that does not know it, which is invisible in the render.
  const transform = pbr.output_transform ?? "srgb";
  if (transform !== "srgb") throw new Error(`scene.json pbr: unsupported output_transform '${String(transform)}'`);

  const rawChannels = (pbr.channels ?? {}) as Record<string, unknown>;
  const channels = {} as { -readonly [K in keyof PbrChannelRanges]: PbrChannelRanges[K] };
  for (const [key, field] of SPANS) channels[field] = requireSpan(rawChannels[key], key, apprChannels);
  if (span(channels.feature) !== featDim) {
    throw new Error(`scene.json pbr: feature span ${span(channels.feature)} disagrees with feat_dim ${featDim}`);
  }
  if (span(channels.albedo) !== 3) throw new Error(`scene.json pbr: albedo must span 3 channels`);

  return { branches, apprChannels, featDim, channels, minRoughness, bsdf, envSh27: readEnvSh27(pbr.env) };
}

/** Channels this branch's appr emits — the material width, or RGB when it is not PBR. */
export function branchApprChannels(pbr: PbrConfig | null, branch: string): number {
  return pbr && pbr.branches.includes(branch) ? pbr.apprChannels : RGB_APPR_CHANNELS;
}

/** Does this branch's appr emit a material? */
export function isPbrBranch(pbr: PbrConfig | null, branch: string): boolean {
  return !!pbr && pbr.branches.includes(branch);
}

function span(range: readonly [number, number]): number {
  return range[1] - range[0];
}

function requirePositiveInt(value: unknown, key: string): number {
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) throw new Error(`scene.json pbr: bad ${key} ${String(value)}`);
  return n;
}

function requireSpan(value: unknown, key: string, channels: number): readonly [number, number] {
  if (!Array.isArray(value) || value.length !== 2) throw new Error(`scene.json pbr: channels.${key} must be [start, end]`);
  const [start, end] = value.map(Number);
  if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end <= start || end > channels) {
    throw new Error(`scene.json pbr: channels.${key} = [${String(value[0])}, ${String(value[1])}] outside 0..${channels}`);
  }
  return [start, end];
}

/** The trained environment, as the 27 floats three.js SH order wants. */
function readEnvSh27(raw: unknown): Float32Array | null {
  if (!raw || typeof raw !== "object") return null;
  const sh = (raw as Record<string, unknown>).sh27;
  if (!Array.isArray(sh)) return null;
  if (sh.length !== 27 || !sh.every((v) => Number.isFinite(Number(v)))) {
    throw new Error(`scene.json pbr: env.sh27 must be 27 finite numbers, got ${sh.length}`);
  }
  return Float32Array.from(sh, Number);
}
